//Built-in libraries
import { useEffect, useState } from 'react';

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

//Custom components
import Button from 'components/UI/Button';
import { HookFileInput } from 'components/UI/HookForm/Form/File';

//Services
import { useFileUpload } from 'hooks/useFileUpload';
import { useApplicationFormContext } from 'features/ApplicationForm/ApplicationFormProvider';
import { compareTwoArrays, isObjectEmpty } from 'utils';
import { signApplicationPublicAsset } from 'api/Rover/ApplicationForm';

//STP Libraries

//Assets and Miscellaneous

import { SignedApplicationAssetResponse } from 'types/Shared';
import { ISignApplicationPublicAssetPayload } from 'api/Rover/ApplicationForm/types';
import { HookRadioGroup } from 'components/UI/HookForm/Form/RadioGroup';
import { HookInput } from 'components/UI/HookForm/Form/Input';
import OrganizationStaff from './OrganizationStaffs';
import { HookTextarea } from 'components/UI/HookForm/Form/TextArea';

//Component Begin

export default function Capacity() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const {
    setCurrentStep,
    updateData,
    data: formContextData,
  } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
  } = useFormContext();

  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        res.forEach((r, resIndex) => {
          const { files, sign } = r.value;
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }

          updateData({
            step5: {
              ...getValues('step5'),
              [payloads[resIndex].field.key]: {
                ...getValues('step5')[payloads[resIndex].field.key],
                selectedFiles: signRes.map((sig, index) => ({
                  name: files[index].name,
                  path: sig.path,
                  key: sig.key,
                  type: files[index].type,
                  field: payloads[resIndex].field.key,
                  description: payloads[resIndex].field.description,
                })),
              },
              staffs: [...formContextData.step5.staffs]
            },
          });
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (error: any) => {
        console.log(error);
        setUploadError('Failed to upload');
      },
    }
  );

  useEffect(() => {
    if (errors?.step5 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step5)[0];
      if(firstErrorKey) setFocus(`step5.${firstErrorKey}`);     
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = () => {
    const formData = getValues('step5');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles && selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step5[
          f
        ].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);
        const fileUploadCompare = compareTwoArrays(
          formDataSelectedFiles,
          formContextDataUploadedFiles
        );

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();

    if (!isValid) {
      return;
    }
    const formData = getValues('step5');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step5: { ...formData, staffs: [...formContextData.step5.staffs] },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>
          Capacity
        </h3>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-bold text-neutralColor-medium col-span-6'>
            Provide evidence that your CHDO conforms to the financial
            accountability standards of 24 CFR 84.21, "Standards for Financial
            Management Systems"
          </h3>
          <HookFileInput
            name='step5.f1.selectedFiles'
            label='Notarized statement'
            id='selectedFiles'
            multiple
          />
          <HookFileInput
            name='step5.f2.selectedFiles'
            label='Certification from a CPA'
            id='selectedFiles'
            multiple
          />
          <HookFileInput
            name='step5.f3.selectedFiles'
            label='HUD approved audit summary'
            id='selectedFiles'
            multiple
          />
        </div>
        <HookInput
          customClassName='col-span-2'
          label='Identify fiscal year'
          name='step5.fiscalYear'
          type='date'
        />
        <div className='col-span-6'>
          <HookRadioGroup
            label='Are you in compliance with all DOH or CHFA agreements?'
            name='step5.hasCompliance'
            options={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
          />
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f4.selectedFiles'
            label='Please provide evidence that you have demonstrated capacity and experience to act as a developer/ owner/ sponsor and carry out the  proposed development activity to be assisted with HOME funds'
            id='selectedFiles'
            multiple
          />
        </div>
        <div className='col-span-6'>
          <OrganizationStaff />
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f5.selectedFiles'
            label='Submit evidence of staff training '
            id='selectedFiles'
            multiple
          />
        </div>
        <div className='col-span-6'>
          <p>
            The CHDO or its parent organization must be able to show one year of
            serving the community prior to the date the Participating
            Jurisdiction provides HOME funds to the organization. The
            organization must describe its history (or its parent organization's
            history) of serving the community by submitting a narrative
            describing activities which it provided (or its parent organization
            provided), such as developing new housing, rehabilitating existing
            stock and managing housing stock, or delivering non-housing services
            that have had lasting benefits for the community, such as
            counseling, food relief, or childcare facilities. The narrative must
            demonstrate that the CHDO has a history of serving the community
            within which housing to be assisted with HOME funds is to be
            located, as evidenced by
          </p>
          <div className='space-y-5 mt-2'>
            <HookFileInput
              name='step5.f6.selectedFiles'
              id='selectedFiles'
              multiple
            />
            <HookTextarea
              id='description'
              name='step5.f6.description'
              label='Description'
              srOnlyLabel
              rows={3}
            />
          </div>
        </div>
        {uploadError && (
          <p className='text-tertiaryColor-normal text-md col-span-6'>
            {uploadError}
          </p>
        )}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button
          label='Previous'
          btnStyle='neutral'
          type='button'
          onClick={() => setCurrentStep((prev) => prev - 1)}
        />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
